/**
 * Carry-capacity pressure levels, shared by the HUD's weight chip and the
 * paperdoll's capacity bars.
 *
 * Thresholds are on **remaining** capacity, not used — "orange at 10% left,
 * red at 1% or less" (user spec). `critical` is therefore checked first, and
 * both comparisons are inclusive.
 *
 * Kept as a pure module (no DOM) so the boundaries are unit-testable — see
 * capacity.test.ts. Two callers need the same answer for weight AND slots:
 * inventory.md rule 3 says the carried bag is blocked by *whichever cap hits
 * first*, so styling weight alone as "the" limit would mislead a player who
 * is actually slot-blocked. Use `worstLevel()` to combine them.
 */

export type CapacityLevel = 'ok' | 'warn' | 'critical';

/** Fraction of capacity still free at which the readout turns orange. */
export const WARN_REMAINING = 0.1;
/** ...and red. */
export const CRITICAL_REMAINING = 0.01;

/**
 * Slack for binary floating-point error. Without it, a cap of 3 with 2.7kg
 * used yields 0.10000000000000002 remaining and silently misses the inclusive
 * 10% boundary — the exact kind of "correct but one ULP off" gap a player
 * reads as a bug.
 */
const EPSILON = 1e-9;

/**
 * `cap <= 0` (or a non-finite input) reports `critical`: no room at all is the
 * most-constrained state, not an absent one. Callers must still guard their own
 * division before rendering a percentage — see `fillPercent`.
 */
export function capacityLevel(used: number, cap: number): CapacityLevel {
  if (!Number.isFinite(used) || !Number.isFinite(cap) || cap <= 0) return 'critical';
  // Clamped: equipping a Heavy piece cuts carryWeight (armour-class penalty),
  // so an already-packed bag can legitimately sit *over* cap — negative
  // remaining must read as critical, not wrap into some other level.
  const remaining = Math.max(0, (cap - used) / cap);
  if (remaining <= CRITICAL_REMAINING + EPSILON) return 'critical';
  if (remaining <= WARN_REMAINING + EPSILON) return 'warn';
  return 'ok';
}

/** Bar width as a 0-100 percentage of capacity used. Safe for cap <= 0 (returns 100). */
export function fillPercent(used: number, cap: number): number {
  if (!Number.isFinite(used) || !Number.isFinite(cap) || cap <= 0) return 100;
  return Math.max(0, Math.min(100, (used / cap) * 100));
}

/** The more constrained of two levels — for "blocked by whichever hits first". */
export function worstLevel(a: CapacityLevel, b: CapacityLevel): CapacityLevel {
  if (a === 'critical' || b === 'critical') return 'critical';
  if (a === 'warn' || b === 'warn') return 'warn';
  return 'ok';
}
